import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { Phone, Mail, Download, FileText, MapPin, Clock, CheckCircle2, Lock, MessageSquare } from 'lucide-react';

const PHONE = '+33782268200';
const PHONE_DISPLAY = '+33 7 82 26 82 00';
const EMAIL = 'contact@code2roues.fr';
const CV_PATH = '/assets/bruno-llorens-cv-2027.pdf';

export default function VCardPage() {
  const { user, loading } = useAuth();
  const cvUrl = `${window.location.origin}${CV_PATH}`;

  if (loading) return <PageShell><div className="max-w-3xl mx-auto px-4 py-20 text-white/50">Chargement…</div></PageShell>;

  const allowed = user && (user.is_admin || ['admin', 'enseignant', 'secretaire', 'secretariat'].includes(user.role));

  if (!allowed) {
    return (
      <PageShell>
        <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="vcard-locked">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/10 items-center justify-center mb-5"><Lock size={22} className="text-orange-400" /></span>
          <h1 className="font-serif-editorial text-white text-[26px] sm:text-[32px]">Espace réservé aux professionnels</h1>
          <p className="text-white/55 text-[13.5px] mt-3 leading-relaxed">
            Cette carte de visite digitale est accessible uniquement aux comptes enseignants et administrateurs.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/" className="h-11 px-5 rounded-full border border-white/15 text-white text-[13px] font-semibold inline-flex items-center hover:bg-white/5">Retour à l'accueil</Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-lg mx-auto px-4 mt-8 sm:mt-12 pb-16" data-testid="vcard-page">
        <div className="relative rounded-3xl bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border border-white/10 shadow-2xl shadow-orange-500/10 overflow-hidden">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-orange-500/20 blur-3xl pointer-events-none" />

          <div className="relative px-6 pt-10 pb-8 text-center">
            {/* Avatar */}
            <div className="relative inline-flex mb-4">
              <span className="absolute inset-0 rounded-full bg-orange-500/40 blur-xl" />
              <span className="relative w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 text-white flex items-center justify-center text-[30px] font-black shadow-2xl" style={{ fontFamily: 'Fraunces, Georgia, serif' }} data-testid="vcard-avatar">
                LB
              </span>
            </div>

            {/* Badge dispo */}
            <div className="flex justify-center mb-3">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold" data-testid="vcard-badge-dispo">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Disponible 7j/7 · 08h-20h
              </span>
            </div>

            <h1 className="text-white text-[28px] sm:text-[32px] leading-tight" style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 700 }} data-testid="vcard-name">Bruno Llorens</h1>
            <p className="text-orange-400 text-[14px] font-semibold mt-1">Enseignant Moto · Code2Roues</p>
            <p className="text-slate-400 text-[12px] mt-1.5">ECSR + CCS Deux Roues · Aut. A 26 038 0007 0 Isère</p>

            {/* Téléphone géant */}
            <a href={`tel:${PHONE}`} className="block mt-6 text-white text-[26px] sm:text-[30px] font-black tracking-tight hover:text-orange-300 transition-colors" data-testid="vcard-phone">
              {PHONE_DISPLAY}
            </a>
            <div className="mt-3 flex justify-center gap-3">
              <a href={`tel:${PHONE}`} data-testid="vcard-call-btn" className="h-11 px-6 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-[13.5px] font-bold inline-flex items-center gap-2 shadow-lg shadow-orange-500/25 transition">
                <Phone size={15} /> Appeler
              </a>
              <a href={`sms:${PHONE}`} data-testid="vcard-sms-btn" className="h-11 px-6 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13.5px] font-semibold inline-flex items-center gap-2 transition">
                <MessageSquare size={15} /> SMS
              </a>
            </div>

            {/* Email + Adresse */}
            <div className="mt-6 space-y-2 text-left">
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition" data-testid="vcard-email">
                <Mail size={16} className="text-orange-400 shrink-0" />
                <span className="text-slate-200 text-[13px] font-medium">{EMAIL}</span>
              </a>
              <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10" data-testid="vcard-address">
                <MapPin size={16} className="text-orange-400 shrink-0" />
                <span className="text-slate-300 text-[12.5px] leading-snug">Saint-Savin (38300) · Nord Isère · Est Lyonnais · Drôme</span>
              </div>
            </div>

            {/* QR codes */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/5 border border-blue-500/30 p-4 flex flex-col items-center" data-testid="vcard-qr-cv">
                <div className="bg-white rounded-xl p-2.5">
                  <QRCodeSVG value={cvUrl} size={110} fgColor="#1d4ed8" bgColor="#ffffff" level="M" />
                </div>
                <span className="mt-2.5 text-blue-300 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1"><FileText size={11} /> Mon CV</span>
              </div>
              <div className="rounded-2xl bg-white/5 border border-orange-500/30 p-4 flex flex-col items-center" data-testid="vcard-qr-call">
                <div className="bg-white rounded-xl p-2.5">
                  <QRCodeSVG value={`tel:${PHONE}`} size={110} fgColor="#ea580c" bgColor="#ffffff" level="M" />
                </div>
                <span className="mt-2.5 text-orange-300 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1"><Phone size={11} /> Appeler</span>
              </div>
            </div>

            {/* CTA CV */}
            <a href={CV_PATH} download="Bruno-Llorens-CV-2027.pdf" data-testid="vcard-cv-download"
              className="mt-5 w-full h-12 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-xl shadow-orange-500/20 transition">
              <Download size={16} /> Télécharger mon CV (PDF · 5 pages)
            </a>

            {/* 3 points pro */}
            <div className="mt-6 space-y-2 text-left">
              {[
                { icon: CheckCircle2, txt: 'Agrément préfectoral valide jusqu\'en 2032' },
                { icon: Clock, txt: 'Disponible 7j/7 · 08h-20h, interventions souples' },
                { icon: MapPin, txt: 'Zone : Nord Isère · Est Lyonnais · Drôme' },
              ].map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="flex items-center gap-2.5 text-slate-300 text-[12.5px]">
                    <Icon size={14} className="text-emerald-400 shrink-0" /> {p.txt}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <p className="text-center text-white/35 text-[11px] mt-4" data-testid="vcard-footer-note">
          Carte de visite pro · également en ligne sur code2roues.fr/vcard
        </p>
      </div>
    </PageShell>
  );
}
